/**
 * URL Helper Utility
 * Centralized URL generation based on environment (development/staging/production)
 *
 * Environment values:
 * - development: localhost URLs
 * - staging: productlogz.in URLs
 * - production: productlogz.com URLs
 */

const NODE_ENV = process.env.REACT_APP_NODE_ENV;

/**
 * Get the base domain based on environment
 * @returns {string} Base domain (localhost:3008, productlogz.in, or productlogz.com)
 */
export const getBaseDomain = () => {
  let baseDomain = 'productlogz.com';
  if (NODE_ENV === 'development') {
    baseDomain = 'localhost:3008';
  } else if (NODE_ENV === 'staging') {
    baseDomain = 'productlogz.in';
  }
  return baseDomain;
};

/**
 * Get the protocol based on environment
 * @returns {string} Protocol (http or https)
 */
export const getProtocol = () => NODE_ENV === 'development' ? 'http' : 'https';

/**
 * Get workspace-specific URL
 * @param {string} path - Path after the subdomain (e.g., 'auth', 'admin/feedback')
 * @param {string} subdomain - Workspace subdomain
 * @returns {string} Full URL with subdomain
 */
export const getWorkspaceUrl = (path, subdomain) => {
  const protocol = getProtocol();
  const domain = getBaseDomain();
  return `${protocol}://${subdomain}.${domain}/${path}`;
};

/**
 * Get app-specific URL (app.productlogz.com)
 * @param {string} path - Path after the domain (e.g., 'login', 'signup')
 * @returns {string} Full URL for app domain
 */
export const getAppUrl = (path) => {
  let appDomain = 'app.productlogz.com';
  if (NODE_ENV === 'development') {
    appDomain = 'localhost:3008';
  } else if (NODE_ENV === 'staging') {
    appDomain = 'staging.productlogz.in';
  }
  return `${getProtocol()}://${appDomain}/${path}`;
};

/**
 * Legacy function name for backward compatibility
 * Generates workspace-specific host URL
 * @param {string} path - Path after the subdomain
 * @param {string} subdomain - Workspace subdomain
 * @returns {string} Full URL with subdomain
 */
export const getHostUrl = (path, subdomain) => getWorkspaceUrl(path, subdomain);

/**
 * Get current base URL for API or current environment
 * @returns {string} Base URL without path
 */
export const getCurrentBaseUrl = () => {
  const protocol = getProtocol();
  const domain = getBaseDomain();
  return `${protocol}://${domain}`;
};

/**
 * Check if current environment is development
 * @returns {boolean}
 */
export const isDevelopment = () => NODE_ENV === 'development';

/**
 * Check if current environment is staging
 * @returns {boolean}
 */
export const isStaging = () => NODE_ENV === 'staging';

/**
 * Check if current environment is production
 * @returns {boolean}
 */
export const isProduction = () => NODE_ENV !== 'development' && NODE_ENV !== 'staging';

/**
 * Get external/marketing URLs based on environment
 * @returns {object} Object containing all external links
 */
export const getExternalLinks = () => {
  const domain = getBaseDomain();
  const protocol = getProtocol();

  return {
    // Help & Documentation
    help: `${protocol}://help.${domain}`,
    helpGettingStarted: `${protocol}://help.${domain}/`,

    // Company/Demo workspace
    companyRoadmap: `${protocol}://company.${domain}/roadmap`,
    companyFeatureRequests: `${protocol}://company.${domain}/feature_requests`,

    // Marketing site
    website: `${protocol}://${domain}`,
    blog: `${protocol}://${domain}/blog`,
    privacy: `${protocol}://${domain}/privacy`,
    terms: `${protocol}://${domain}/terms`,

    // App URLs
    appLogin: `${protocol}://app.${domain}/login`,
    appSignup: `${protocol}://app.${domain}/signup`,

    // Powered by link with UTM params
    poweredBy: (companyID) =>
      `${protocol}://${domain}?utm_source=roadmap&utm_medium=powered&utm_campaign=${companyID}&company=${companyID}`,
  };
};

/**
 * Build subdomain URL dynamically (for legacy redirects)
 * @param {string} subdomain - Company subdomain
 * @param {string} path - Optional path after domain
 * @returns {string} Full subdomain URL
 */
export const getSubdomainUrl = (subdomain, path = '') => {
  const domain = getBaseDomain();
  const protocol = getProtocol();
  return `${protocol}://${subdomain}.${domain}${path}`;
};
